import type { ReportCardSummary } from "./reportCard.ts";

const STORAGE_KEY = "f15m_report_cards";
const MAX_ENTRIES = 30;

type ReadableStorage = Pick<Storage, "getItem" | "setItem">;

function storage(): ReadableStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadReportCardLog(source: ReadableStorage | null = storage()): ReportCardSummary[] {
  if (!source) return [];
  try {
    const raw = source.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isSummary) : [];
  } catch {
    return [];
  }
}

function isSummary(value: unknown): value is ReportCardSummary {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.at === "number" &&
    typeof row.title === "string" &&
    typeof row.word === "string" &&
    typeof row.openingTitle === "string"
  );
}

export function appendReportCardLog(
  entry: ReportCardSummary,
  source: ReadableStorage | null = storage()
): ReportCardSummary[] {
  const kept = loadReportCardLog(source).filter((row) => !entry.key || row.key !== entry.key);
  const next = [...kept, entry].slice(-MAX_ENTRIES);
  if (source) {
    try {
      source.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* Storage can be unavailable in private browsing; the card still shows. */
    }
  }
  return next;
}

export function clearReportCardLog(source: ReadableStorage | null = storage()): void {
  if (!source) return;
  try {
    source.setItem(STORAGE_KEY, "[]");
  } catch {
    /* ignore */
  }
}
